import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { http } from '../api/http';
import { getAuthUser } from '../auth/session';
import { sectionForPath } from '../navigation';

const AssistantContext = createContext(null);

function listIdFromPath(pathname) {
  const match = String(pathname || '').match(/^\/lists\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

// Where a result should send the user when the server did not say explicitly.
function destinationForResult(data) {
  const mode = String(data?.mode || '');
  const query = String(data?.query || '').trim();
  const category = String(data?.category || '').trim();
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (category) params.set('category', category);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  if (mode === 'market') return `/market${suffix}`;
  if (mode === 'inventory' || mode === 'resale') return `/inventory${suffix}`;
  if (mode === 'services') return `/services${suffix}`;
  if (mode === 'groceries') return '/lists/groceries';
  if (mode === 'lists' || mode === 'quick_list') {
    const id = String(data?.list?.id || '').trim();
    return data?.list?.kind === 'grocery' ? '/lists/groceries' : id ? `/lists/${encodeURIComponent(id)}` : '/lists';
  }
  return '';
}

export function AssistantProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const assistantNavRef = useRef('');

  useEffect(() => {
    // A result stays visible on the page it belongs to. Any navigation the
    // user makes on their own clears it so stale text never lingers.
    if (!answer) return;
    const pinned = String(assistantNavRef.current || answer.path || '');
    const current = `${location.pathname}${location.search}`;
    if (pinned && current !== pinned && location.pathname !== pinned.split('?')[0]) {
      setAnswer(null);
      assistantNavRef.current = '';
    }
  }, [answer, location.pathname, location.search]);

  const clearAnswer = useCallback(() => {
    setAnswer(null);
    assistantNavRef.current = '';
  }, []);

  const applyResult = useCallback((data, { text }) => {
    const destination = String(data?.navigate || destinationForResult(data) || '').trim();
    const answerText = String(data?.answer || '').trim();
    const path = destination || `${location.pathname}${location.search}`;
    setLastResult(data);
    if (answerText) {
      assistantNavRef.current = path;
      setAnswer({
        text: answerText,
        mode: String(data?.mode || ''),
        tone: data?.success === false ? 'warning' : 'info',
        query: text,
        path,
        atMs: Date.now(),
      });
    } else {
      setAnswer(null);
      assistantNavRef.current = '';
    }
    try {
      window.dispatchEvent(new CustomEvent('needl:agent-action', { detail: data }));
    } catch {
      // Older browsers without CustomEvent constructors simply skip the broadcast.
    }
    if (destination && destination !== `${location.pathname}${location.search}`) navigate(destination);
  }, [location.pathname, location.search, navigate]);

  const submit = useCallback(async (rawText) => {
    const text = String(rawText || '').replace(/\s+/g, ' ').trim();
    if (!text || pending) return null;
    const section = sectionForPath(location.pathname);
    const authed = getAuthUser();
    if (!authed) {
      const target = section === 'services' ? '/services' : '/market';
      navigate(`${target}?q=${encodeURIComponent(text)}`);
      return null;
    }
    const params = new URLSearchParams(location.search || '');
    setPending(true);
    try {
      const res = await http.post('/api/assistant/query', {
        text,
        section,
        category: params.get('category') || '',
        listId: listIdFromPath(location.pathname),
      });
      applyResult(res.data || {}, { text });
      return res.data || null;
    } catch (error) {
      const message = error?.data?.message || error?.message || 'needL could not do that right now.';
      setAnswer({ text: message, mode: section, tone: 'error', query: text, path: `${location.pathname}${location.search}`, atMs: Date.now() });
      assistantNavRef.current = `${location.pathname}${location.search}`;
      return null;
    } finally {
      setPending(false);
    }
  }, [applyResult, location.pathname, location.search, navigate, pending]);

  const value = useMemo(() => ({ pending, answer, lastResult, submit, clearAnswer }), [answer, clearAnswer, lastResult, pending, submit]);

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant() {
  return useContext(AssistantContext) || { pending: false, answer: null, lastResult: null, submit: async () => null, clearAnswer: () => {} };
}
